import { useState, useRef, useCallback } from 'react';
import { AddressAutocomplete } from './address-autocomplete';
import { Input } from './input';
import { Label } from './label';
import type { AddressSuggestion } from './address-autocomplete';

interface AddressFormProps {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

interface ParsedAddress {
  street: string;
  buildingNumber: string;
  apartmentNumber: string;
}

function parseAddress(address: string): ParsedAddress {
  if (!address) {
    return { street: '', buildingNumber: '', apartmentNumber: '' };
  }

  const buildingMatch = address.match(/(.+?)\s+(\d+[A-Za-z]*)(?:\/(\d+[A-Za-z]*))?$/);

  if (buildingMatch) {
    return {
      street: buildingMatch[1].trim(),
      buildingNumber: buildingMatch[2] || '',
      apartmentNumber: buildingMatch[3] || '',
    };
  }

  return {
    street: address.trim(),
    buildingNumber: '',
    apartmentNumber: '',
  };
}

function combineAddress(street: string, buildingNumber: string, apartmentNumber: string): string {
  const parts: string[] = [];

  if (street) parts.push(street);
  if (buildingNumber) {
    if (apartmentNumber) {
      parts.push(`${buildingNumber}/${apartmentNumber}`);
    } else {
      parts.push(buildingNumber);
    }
  }

  return parts.join(' ');
}

export function AddressForm({ value, onValueChange, disabled = false, className }: AddressFormProps) {
  const parsed = parseAddress(value);
  const [street, setStreet] = useState(parsed.street);
  const [buildingNumber, setBuildingNumber] = useState(parsed.buildingNumber);
  const [apartmentNumber, setApartmentNumber] = useState(parsed.apartmentNumber);

  // Track internal values to avoid re-render loops from parent value sync
  const streetRef = useRef(street);
  const buildingRef = useRef(buildingNumber);
  const apartmentRef = useRef(apartmentNumber);

  const notifyParent = useCallback(() => {
    const combined = combineAddress(streetRef.current, buildingRef.current, apartmentRef.current);
    onValueChange(combined);
  }, [onValueChange]);

  const handleStreetChange = useCallback((val: string) => {
    streetRef.current = val;
    setStreet(val);
  }, []);

  const handleBuildingChange = useCallback((val: string) => {
    buildingRef.current = val;
    setBuildingNumber(val);
  }, []);

  const handleApartmentChange = useCallback((val: string) => {
    apartmentRef.current = val;
    setApartmentNumber(val);
  }, []);

  const flushToParent = notifyParent;

  const handleAddressSelect = (selectedAddress: string, details?: AddressSuggestion) => {
    if (details?.address_components) {
      const components = details.address_components;
      const streetNumber = components.long_name || '';
      const route = selectedAddress.split(',')[0] || '';
      const parsed = parseAddress(selectedAddress);

      const streetParts: string[] = [];
      if (route && !route.match(/^\d+/)) {
        streetParts.push(route);
      } else if (parsed.street) {
        streetParts.push(parsed.street);
      }

      handleStreetChange(streetParts.join(', '));

      if (streetNumber && !buildingRef.current) {
        handleBuildingChange(streetNumber);
      } else if (parsed.buildingNumber && !buildingRef.current) {
        handleBuildingChange(parsed.buildingNumber);
      }
    } else {
      const parsed = parseAddress(selectedAddress);
      handleStreetChange(parsed.street);
      if (parsed.buildingNumber && !buildingRef.current) {
        handleBuildingChange(parsed.buildingNumber);
      }
    }
    notifyParent();
  };

  return (
    <div className={className}>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="address-street">Street Address</Label>
          <AddressAutocomplete
            id="address-street"
            value={street}
            onValueChange={handleStreetChange}
            onSelect={handleAddressSelect}
            onBlur={flushToParent}
            disabled={disabled}
            placeholder="Search street, city..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="address-building">Building Number</Label>
            <Input
              id="address-building"
              type="text"
              value={buildingNumber}
              onChange={(e) => handleBuildingChange(e.target.value)}
              onBlur={flushToParent}
              disabled={disabled}
              placeholder="e.g., 40A"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address-apartment">Apartment/Unit</Label>
            <Input
              id="address-apartment"
              type="text"
              value={apartmentNumber}
              onChange={(e) => handleApartmentChange(e.target.value)}
              onBlur={flushToParent}
              disabled={disabled}
              placeholder="e.g., 1"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
